import z from 'zod';
/**
 * Commercial Bill of Sale "purchase" events for a project (DR-1762 / CT).
 *
 * Emitted as ONE combined webhook per project — every Bill-of-Sale document's
 * events are packed into a single `billOfSaleEvents` array so consumers can draw
 * a single ownership-chain table (mirrors the engine-events "one per project"
 * shape). Because the payload spans multiple source documents, the source lives
 * on each event (`documentId` + `pageId`), not at the top level. Brain filters
 * legs to the project's working engine before emitting (DR-1801): a Brain
 * project maps 1:1 to one Oil pipeline, so a multi-engine bill's sibling-engine
 * legs are dropped at the source rather than leaked into this pipeline's trace.
 * Blank-ESN legs are kept (some same-engine bills carry no serial).
 *
 * Topic + array name follow brain's data-domain naming (`bill-of-sale-events-ready`
 * / `billOfSaleEvents`), NOT the Oil "commercial trace" feature name — Oil
 * already consumes this exact shape (DR-1704, PR #287).
 */
export const billOfSaleEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('bill-of-sale-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    billOfSaleEvents: z.array(z.looseObject({
        eventType: z.literal('purchase'),
        esn: z.string(),
        seller: z.string().optional(),
        purchaser: z.string().optional(),
        date: z.string().optional(),
        // Whole-bill signed flag (OR across the reconstructed document's pages —
        // the signature usually sits on a different page than the row). Denormalized
        // onto every leg of a multi-engine bill.
        isDocumentSigned: z.boolean(),
        documentId: z.string(),
        // The stable reconstructed-bill id + its full composing page list (DR-1954).
        // A Bill of Sale spans ~2 pages (signature on the last) — link the Source to
        // the whole reconstructed document, not the single body page.
        reconstructedDocumentId: z.string(),
        pageIds: z.array(z.string()),
        // @deprecated body page only (misses the signature page). Use pageIds /
        // reconstructedDocumentId. TODO(DR-1954): remove once v2 reads pageIds.
        pageId: z.string(),
    })),
});
//# sourceMappingURL=bill-of-sale-events-ready.webhook.js.map